import { supabase } from '../lib/supabase';
import { useWalletStore } from '../stores/walletStore';

// Thin client for the server-side AI proxy (Supabase Edge Function `ai-proxy`).
// The Anthropic key lives ONLY in the function's secrets; nothing here touches it.

export interface ProxyMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface ProxyRequest {
  /**
   * Server-side prompt key (Phase 1, AI_ASSISTANT_ARCHITECTURE §4a) —
   * 'assistant.chat', 'builder.quest', 'assessment.talent', … The proxy
   * assembles the system prompt from its own plain-text module; the client
   * sends only DATA (message/conversation/context/params), never
   * instructions. When set, `system`/`messages` are not sent.
   */
  mode?: string;
  /** The new user message (appended after `conversation`). */
  message?: string;
  /** Prior turns, oldest-first (chat only). */
  conversation?: ProxyMessage[];
  /** User-context block; the server appends it under "## Current user context". */
  context?: string;
  /** {{placeholder}} substitutions for the server prompt (today, reference, …). */
  params?: Record<string, string>;
  /**
   * ⭐⭐ The read tools this user permits, from `constants/aiVisibility.ts`.
   *
   * ⚠️⚠️ The read tools run SERVER-SIDE, so trimming the context block cannot
   * stop the model calling one — this is the only half of the visibility
   * switches the client cannot enforce on its own.
   * ⚠️ OMITTED means "no restriction", so an older bundle keeps working; an
   * explicit `[]` means the user refused everything. They are different
   * statements and the proxy must keep telling them apart.
   */
  allowedTools?: string[];
  /** Legacy path: full client-built system prompt (being retired). */
  system?: string;
  /** Legacy path: full messages array (being retired; use mode+message). */
  messages?: ProxyMessage[];
  json?: boolean;          // ask the model for raw JSON output
  maxTokens?: number;
  temperature?: number;
  /**
   * Coin-priced action key (ai_action_prices.key). When set AND that price is
   * ACTIVE in the console, the server debits the wallet server-side (and 402s
   * if the balance is short). Inactive/unknown key → free, as today.
   */
  action?: string;
}

/**
 * A write-tool call the model made (Phase 2 tool-use) — NOT yet executed.
 * The user confirms it in ProposalPreview; the client dispatcher
 * (services/aiProposalApply) charges spend_coin(capability's price key) and
 * runs the stores.
 */
export interface AIProposal {
  /** Anthropic tool_use id — stable key for the proposal card. */
  id: string;
  /** Capability name, e.g. 'create_quest' (see ai-proxy capabilities registry). */
  capability: string;
  /** Schema-validated tool input (shape per capability). */
  input: any;
}

export interface ProxyResult {
  content: string;
  usage?: { used: number; limit: number };
  /** New wallet balance after any Coin debit (present when the action charged). */
  balanceCp?: number;
  /** Write-tool proposals awaiting user confirmation (Phase 2). */
  proposals?: AIProposal[];
}

export class AIProxyError extends Error {
  status: number;
  rateLimited: boolean;
  /** True on 402 — not enough Coin for a charged action. */
  insufficientCoin: boolean;
  /** Daily usage, when the server reports it (e.g. on a 429). */
  usage?: { used: number; limit: number };
  /** Only the server body flag identifies an unfinished model response. */
  truncated: boolean;
  constructor(message: string, status: number, usage?: { used: number; limit: number }, truncated = false) {
    super(message);
    this.name = 'AIProxyError';
    this.status = status;
    this.rateLimited = status === 429;
    this.insufficientCoin = status === 402;
    this.usage = usage;
    this.truncated = truncated;
  }
}

/**
 * ⚠️⚠️ **HOW LONG A 503 SUPPRESSES FURTHER CALLS.** It used to be forever — a
 * single 503 set a boolean that nothing ever cleared, so every AI feature in
 * that tab returned its offline fallback until the page was reloaded. Nobody was
 * told. The owner hit exactly this (2026-09-12): a regeneration produced a
 * composed template, left no trace in any table because no request was sent, and
 * only a reload fixed it.
 *
 * ⭐ Five minutes is long enough to stop hammering a server that is genuinely
 * misconfigured, and short enough that fixing the config — or a transient blip
 * passing — does not require the user to know to reload.
 */
const UNAVAILABLE_FOR_MS = 5 * 60 * 1000;

class AIProxy {
  // True once the server actually answers (used to decide real-vs-mock). We can't
  // see the secret from the client, so "available" means a non-503 response.
  private serverConfigured: boolean | null = null;
  /** When the suppression lapses. `null` = not suppressed. */
  private unavailableUntil: number | null = null;

  /**
   * ⚠️ Callers use this to skip the network and compose offline. It must EXPIRE:
   * a permanent latch turns one bad minute into a dead session.
   */
  isKnownUnavailable(): boolean {
    if (this.unavailableUntil === null) return false;
    if (Date.now() >= this.unavailableUntil) {
      // ⭐ Lapsed — clear it so the next call actually tries.
      this.unavailableUntil = null;
      this.serverConfigured = null;
      return false;
    }
    return true;
  }

  /**
   * ⭐⭐ **SO A SURFACE CAN SAY WHY IT COMPOSED.** Without this the UI can only
   * report what it produced ("composed from your own entries") and not that the
   * model was never asked — which reads as a choice the app made rather than a
   * failure it hit.
   */
  isSuppressedAfterFailure(): boolean {
    return this.isKnownUnavailable();
  }

  async chat(req: ProxyRequest): Promise<ProxyResult> {
    const { data, error } = await supabase.functions.invoke('ai-proxy', {
      body: req.mode
        ? {
            mode: req.mode,
            message: req.message,
            conversation: req.conversation,
            context: req.context,
            params: req.params,
            allowedTools: req.allowedTools,
            json: req.json ?? false,
            max_tokens: req.maxTokens,
            temperature: req.temperature,
            action: req.action,
          }
        : {
            system: req.system,
            messages: req.messages,
            json: req.json ?? false,
            max_tokens: req.maxTokens,
            temperature: req.temperature,
            action: req.action,
          },
    });

    // supabase-js returns a FunctionsHttpError for non-2xx; pull the JSON body out.
    if (error) {
      const ctx: any = (error as any).context;
      const status = ctx?.status ?? 500;
      let message = error.message;
      let truncated = false;
      let usage: { used: number; limit: number } | undefined;
      try {
        const bodyJson = ctx && typeof ctx.json === 'function' ? await ctx.json() : null;
        if (bodyJson?.error) message = bodyJson.error;
        if (bodyJson?.usage) usage = bodyJson.usage;
        truncated = bodyJson?.truncated === true;
      } catch {
        /* ignore parse failure */
      }
      if (status === 503) {
        this.serverConfigured = false;
        // ⚠️ A WINDOW, not a permanent flag. See `UNAVAILABLE_FOR_MS`.
        this.unavailableUntil = Date.now() + UNAVAILABLE_FOR_MS;
      }
      throw new AIProxyError(message, status, usage, truncated);
    }

    this.serverConfigured = true;
    // ⭐ A success clears any suppression immediately — the server is plainly up.
    this.unavailableUntil = null;
    // A charged action returns the new balance → refresh the wallet so the
    // balance + activity update wherever they're shown.
    if (typeof data?.balance_cp === 'number') {
      void useWalletStore.getState().refresh();
    }
    return {
      content: data?.content ?? '',
      usage: data?.usage,
      balanceCp: data?.balance_cp,
      proposals: Array.isArray(data?.proposals) ? data.proposals : undefined,
    };
  }
}

export const aiProxy = new AIProxy();
